import type { ComponentProps } from "react";

import { css } from "@compiled/react";

import Button from "@atlaskit/button";
import StatusWarningIcon from "@atlaskit/icon/core/status-warning";
import RandomizeIcon from "@atlaskit/icon-lab/core/randomize";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import { PreviewTable } from "@/templates/paid-landing/sections/cfind-4666-frictionless-first-look/paid-jira-ai-preview-modal/preview-table";
import { getCgsKeyFromLocationHref } from "@/templates/paid-landing/utils/paid-jira-ai-analytics";

const inlinePreviewSectionCss = css({
    marginBlockStart: "98px",
    marginBlockEnd: "56px",
    padding: "0 20px",
    position: "relative",
    isolation: "isolate"
});

/* eslint-disable local/ensure-wac-token-usage */
const inlinePreviewBackgroundCss = css({
    zIndex: 0,
    position: "absolute",
    insetBlockStart: -120,
    insetInlineStart: "50%",
    transform: "translateX(-50%)",
    width: "100%",
    height: 566,
    pointerEvents: "none",
    // eslint-disable-next-line local/ensure-wac-token-usage
    background:
        "var(--Gradient, conic-gradient(from 180deg at 50% 50%, #FCA700 1.7999999597668648deg, #82B536 70.19999742507935deg, #1868DB 128.85337471961975deg, #BF63F3 223.20000171661377deg, #F0F1F2 316.79999828338623deg, #FCA700 352.8000068664551deg))",
    opacity: 0.2,
    clipPath: "polygon(0% 16%, 100% 0%, 100% 97%, 0% 100%)"
});
/* eslint-enable local/ensure-wac-token-usage */

const inlinePreviewBackgroundErrorCss = css({
    height: 640
});

const inlinePreviewContentCss = css({
    position: "relative",
    zIndex: 1
});

const inlinePreviewStatusCss = css({
    width: "886px",
    maxWidth: "100%",
    margin: "0 auto 28px auto",
    background: wacToken("color.neutral.light0"),
    borderRadius: "16px",
    boxShadow:
        // eslint-disable-next-line local/ensure-wac-token-usage
        "0px 0px 0.5px rgba(30, 31, 33, 0.31), 0px 8px 6px rgba(30, 31, 33, 0.15)",
    padding: "8px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "16px"
});

const inlinePreviewStatusLoadingBorderCss = css({
    position: "relative",
    overflow: "hidden"
});

/* eslint-disable local/ensure-wac-token-usage */
const inlinePreviewStatusLoadingBorderLayerCss = css({
    position: "absolute",
    inset: 0,
    padding: "1px",
    borderRadius: "inherit",
    backgroundImage:
        "linear-gradient(to right, #1F69DA 7%, #C16CD4 29%, #F5A117 65%, #79AE44 85%)",
    opacity: 0.7,
    WebkitMask:
        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    pointerEvents: "none"
});
/* eslint-enable local/ensure-wac-token-usage */

const inlinePreviewSummaryTextCss = css({
    fontSize: "14px",
    lineHeight: "20px",
    color: wacToken("color.primary.black"),
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const inlinePreviewSummaryRegularTextCss = css({
    fontWeight: 400,
    letterSpacing: "0.3px"
});

const inlinePreviewSummaryEmphasisTextCss = css({
    fontWeight: 600,
    letterSpacing: "0.3px"
});

const inlinePreviewWorkingTextCss = css({
    fontSize: "14px",
    lineHeight: "20px",
    color: wacToken("color.primary.black")
});

const inlinePreviewErrorCss = css({
    width: "886px",
    maxWidth: "100%",
    margin: "0 auto 16px auto",
    // eslint-disable-next-line local/ensure-wac-token-usage
    border: "2px solid #ffaa00",
    // eslint-disable-next-line local/ensure-wac-token-usage
    background: "#f8f4ec",
    fontSize: "14px",
    lineHeight: "1.25",
    padding: "1rem",
    color: wacToken("color.primary.black"),
    display: "flex",
    gap: "1rem",
    alignItems: "center",
    borderRadius: "0.5rem"
});

const tryAnotherPromptButtonStyles = {
    borderRadius: "40px",
    paddingLeft: "4px",
    paddingRight: "4px",
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 400,
    alignItems: "center"
};

const signupLinkCss = css({
    "color": wacToken("color.extended.blue700"),
    "textDecoration": "underline",
    "textUnderlineOffset": "2px",
    "fontWeight": 600,
    "&:hover": {
        color: wacToken("color.extended.blue900")
    }
});

export const getPromptSummary = ({
    workingOnValue,
    workingOnSuggestion,
    goalValue,
    goalSuggestion
}: {
    workingOnValue: string;
    workingOnSuggestion: string;
    goalValue: string;
    goalSuggestion: string;
}) => {
    const workingOn = (workingOnValue || workingOnSuggestion).trim();
    const goal = (goalValue || goalSuggestion).trim();
    if (!workingOn || !goal) {
        return "I'm working on a new project with the goal of driving stronger outcomes.";
    }
    return `I'm working on ${workingOn} with the goal of ${goal}...`;
};

export const shouldShowInlinePreview = ({
    cgsDataLoading,
    formattedCgsData
}: {
    cgsDataLoading: boolean;
    formattedCgsData?: ComponentProps<typeof PreviewTable>["rows"];
}) => cgsDataLoading || Boolean(formattedCgsData?.length);

interface InlinePreviewProps {
    cgsDataLoading: boolean;
    isErrorFromCgs?: boolean;
    hasSubmittedDefaultValues?: boolean;
    promptSummary: string;
    formattedCgsData?: ComponentProps<typeof PreviewTable>["rows"];
    spaceName?: string;
    onTryAnotherPrompt: () => void;
}

export const InlinePreview = ({
    cgsDataLoading,
    isErrorFromCgs,
    hasSubmittedDefaultValues,
    promptSummary,
    formattedCgsData,
    spaceName,
    onTryAnotherPrompt
}: InlinePreviewProps) => {
    const promptSummaryMatch =
        /^I'm working on (.+) with the goal of (.+)\.\.\.$/.exec(promptSummary);
    const localizedSignupHref = useLocalizedHref(
        "/try/cloud/signup?bundle=jira-software&edition=free"
    );
    const cgsKey =
        typeof window !== "undefined"
            ? getCgsKeyFromLocationHref(window.location.href)
            : undefined;
    const signupHref = cgsKey
        ? `${localizedSignupHref}&cgsKey=${encodeURIComponent(cgsKey)}`
        : localizedSignupHref;

    return (
        <section css={inlinePreviewSectionCss}>
            <div
                css={[
                    inlinePreviewBackgroundCss,
                    isErrorFromCgs && inlinePreviewBackgroundErrorCss
                ]}
            />
            <div css={inlinePreviewContentCss}>
                <div
                    css={[
                        inlinePreviewStatusCss,
                        cgsDataLoading && inlinePreviewStatusLoadingBorderCss
                    ]}
                >
                    {cgsDataLoading && (
                        <>
                            <div
                                css={inlinePreviewStatusLoadingBorderLayerCss}
                                aria-hidden="true"
                            />
                        </>
                    )}
                    {cgsDataLoading ? (
                        <>
                            <p css={inlinePreviewSummaryTextCss}>
                                {promptSummaryMatch ? (
                                    <>
                                        <span
                                            css={
                                                inlinePreviewSummaryRegularTextCss
                                            }
                                        >
                                            I&apos;m working on
                                        </span>
                                        <span
                                            css={
                                                inlinePreviewSummaryEmphasisTextCss
                                            }
                                        >
                                            {" " + promptSummaryMatch[1] + " "}
                                        </span>
                                        <span
                                            css={
                                                inlinePreviewSummaryRegularTextCss
                                            }
                                        >
                                            with the goal of
                                        </span>
                                        <span
                                            css={
                                                inlinePreviewSummaryEmphasisTextCss
                                            }
                                        >
                                            {" " +
                                                promptSummaryMatch[2] +
                                                "..."}
                                        </span>
                                    </>
                                ) : (
                                    promptSummary
                                )}
                            </p>
                            <span css={inlinePreviewWorkingTextCss}>
                                Working...
                            </span>
                        </>
                    ) : (
                        <>
                            <p css={inlinePreviewSummaryTextCss}>
                                Your Jira space is ready.{" "}
                                <a
                                    href={signupHref}
                                    css={signupLinkCss}
                                    data-event="clicked"
                                    data-event-container="inlinePreview"
                                    data-event-component="signupLink"
                                    data-product-key="jiraSoftware-PaidLpJiraFflV2"
                                >
                                    Sign up
                                </a>{" "}
                                to save your progress and make it your own.
                            </p>
                            <Button
                                appearance="subtle"
                                iconBefore={
                                    <RandomizeIcon label="Try another prompt" />
                                }
                                style={tryAnotherPromptButtonStyles}
                                onClick={onTryAnotherPrompt}
                            >
                                Try another prompt
                            </Button>
                        </>
                    )}
                </div>
                {isErrorFromCgs && !hasSubmittedDefaultValues && (
                    <div css={inlinePreviewErrorCss}>
                        <StatusWarningIcon
                            label="error"
                            color={token("color.icon.warning")}
                        />
                        <div>
                            We couldn&apos;t build the project the way you asked
                            for it. So we have built out a project that
                            showcases what Rovo and Jira are capable of.
                        </div>
                    </div>
                )}
                <PreviewTable
                    rows={cgsDataLoading ? undefined : formattedCgsData}
                    spaceName={cgsDataLoading ? undefined : spaceName}
                />
            </div>
        </section>
    );
};
